/**
 * Account sheet: how a web guest keeps their level and record, and how a signed-in player signs out
 * (docs/accounts/PLAN.md).
 *
 * Web only. Inside the Discord Activity everyone is already a Discord user, so nothing in this file
 * renders there. It never gates play either: the sheet is only ever opened from a prompt, at the
 * moments a guest has something to lose — the end of a match, their record, Settings.
 *
 * Two ways in, each shown only when `/api/auth/config` says the server can do it:
 *  - Continue with Discord: a full-page OAuth redirect. The callback lands on `/play?auth=discord`
 *    and `bridge/identity.ts` finishes it, so there is nothing to wait for here.
 *  - Email me a link: the link arrives as `/play#login=<token>`, also finished by identity.ts —
 *    usually in another tab, which is why this sheet only ever gets as far as "check your inbox".
 *
 * The address typed here is sent to the server and kept in this component's state, nowhere else.
 */
import { type FormEvent, useEffect, useRef, useState } from 'react';
import {
  type AuthConfig,
  type ClaimWhere,
  NO_AUTH,
  canPickName,
  discordWebUrl,
  emailStartFailureText,
  fetchAuthConfig,
  signOut,
  startEmailLogin,
  trackAccountEvent,
} from '../../net/accountApi';
import { useStore } from '../../store/store';
import { Button, Sheet } from '../ui';
import { PickNameSheet } from './PickName';

/** How long "Send again" stays disabled after a link goes out. */
export const RESEND_COOLDOWN_MS = 30_000;

/**
 * The server's offer, or null while it is being asked. `override` lets the design lab and tests
 * show a sheet without a server behind it.
 */
export function useAuthConfig(override?: AuthConfig): AuthConfig | null {
  const platform = useStore((s) => s.session.platform);
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const skip = !!override || platform !== 'web';
  useEffect(() => {
    if (skip) return;
    let alive = true;
    void fetchAuthConfig().then((c) => alive && setConfig(c));
    return () => {
      alive = false;
    };
  }, [skip]);
  if (platform !== 'web') return NO_AUTH;
  return override ?? config;
}

/**
 * Should a guest be asked to save their progress here? Web, a guest session, and at least one way
 * in on offer. False while the config is still loading, so a prompt never flashes up and away.
 */
export function useClaimOffer(override?: AuthConfig): boolean {
  const kind = useStore((s) => s.session.me?.kind);
  const config = useAuthConfig(override);
  return kind === 'guest' && !!config && (config.discordWeb || config.email);
}

export interface AccountSheetProps {
  open: boolean;
  onClose: () => void;
  /** Which prompt opened it, for `claim_started`. */
  where: ClaimWhere;
  /** Design lab / tests: skip `/api/auth/config`. */
  config?: AuthConfig;
}

export function AccountSheet({ open, onClose, where, config: override }: AccountSheetProps) {
  const me = useStore((s) => s.session.me);
  const config = useAuthConfig(override);
  const signedIn = me?.kind === 'discord' || me?.kind === 'email';

  return (
    <Sheet open={open} title={signedIn ? 'Account' : 'Save your progress'} onClose={onClose} testId="account">
      {signedIn ? (
        <SignedIn kind={me.kind === 'discord' ? 'discord' : 'email'} name={me.name} />
      ) : config == null ? (
        <p className="profile__notice muted" data-testid="account-loading">
          One moment…
        </p>
      ) : (
        <GuestOptions config={config} where={where} />
      )}
    </Sheet>
  );
}

function SignedIn({ kind, name }: { kind: 'discord' | 'email'; name: string }) {
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  return (
    <div className="stack account" data-testid="account-signed-in">
      <p style={{ margin: 0 }} data-testid="account-method">
        Signed in with {kind === 'discord' ? 'Discord' : 'email'}.
      </p>
      <p className="small muted" style={{ margin: 0 }}>
        You're playing as {name}. Your level and record follow you to any device you sign in on.
      </p>
      {/* Still wearing the guest name: the player said Later to the prompt, and this is the way back. */}
      {canPickName({ kind, name }) && (
        <>
          <Button variant="primary" block testId="account-pick-name" onClick={() => setPicking(true)}>
            Pick your name
          </Button>
          <PickNameSheet open={picking} onClose={() => setPicking(false)} />
        </>
      )}
      <Button
        variant="ghost"
        block
        testId="account-sign-out"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void signOut();
        }}
      >
        {busy ? 'Signing out…' : 'Sign out'}
      </Button>
      <p className="small muted" style={{ margin: 0, textAlign: 'center' }}>
        This browser goes back to a fresh guest.
      </p>
    </div>
  );
}

type EmailState = { step: 'form'; error: string | null } | { step: 'sending' } | { step: 'sent'; to: string; at: number };

function GuestOptions({ config, where }: { config: AuthConfig; where: ClaimWhere }) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<EmailState>({ step: 'form', error: null });

  if (!config.discordWeb && !config.email) {
    return (
      <p className="profile__notice muted" data-testid="account-unavailable">
        Saving progress is offline right now. Your games still count on this browser.
      </p>
    );
  }

  const send = async (to: string) => {
    trackAccountEvent({ ev: 'claim_started', props: { method: 'email', where } });
    setState({ step: 'sending' });
    const res = await startEmailLogin(to);
    setState(res.ok ? { step: 'sent', to, at: Date.now() } : { step: 'form', error: emailStartFailureText(res.reason) });
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const to = email.trim();
    if (!to.includes('@')) {
      setState({ step: 'form', error: emailStartFailureText('invalid') });
      return;
    }
    void send(to);
  };

  return (
    <div className="stack account" data-testid="account-guest">
      <p className="muted" style={{ margin: 0 }}>
        Keep your level and record on any device.
      </p>

      {config.discordWeb && (
        <Button
          variant="primary"
          size="lg"
          block
          testId="account-discord"
          onClick={() => {
            trackAccountEvent({ ev: 'claim_started', props: { method: 'discord', where } });
            location.assign(discordWebUrl());
          }}
        >
          Continue with Discord
        </Button>
      )}

      {config.discordWeb && config.email && (
        <div className="account__divider" aria-hidden>
          <span>or</span>
        </div>
      )}

      {config.email &&
        (state.step === 'sent' ? (
          <EmailSent to={state.to} sentAt={state.at} onResend={() => void send(state.to)} onChange={() => setState({ step: 'form', error: null })} />
        ) : (
          <form className="stack account__form" onSubmit={onSubmit} noValidate>
            <label className="small muted" htmlFor="account-email">
              Email
            </label>
            <input
              id="account-email"
              className="input"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="off"
              spellCheck={false}
              maxLength={320}
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={state.step === 'form' && state.error ? true : undefined}
              aria-describedby={state.step === 'form' && state.error ? 'account-email-error' : undefined}
              data-testid="account-email"
            />
            {state.step === 'form' && state.error && (
              <p id="account-email-error" className="small account__error" role="alert" data-testid="account-email-error">
                {state.error}
              </p>
            )}
            <Button type="submit" block disabled={state.step === 'sending'} testId="account-email-send">
              {state.step === 'sending' ? 'Sending…' : 'Email me a link'}
            </Button>
          </form>
        ))}

      <p className="small muted" style={{ margin: 0, textAlign: 'center' }}>
        Your address is never shown to other players.
      </p>
    </div>
  );
}

/**
 * After the link goes out. The address is repeated back because a typo is the likeliest reason a
 * link never arrives, and this is the only place the player can catch it.
 */
function EmailSent({ to, sentAt, onResend, onChange }: { to: string; sentAt: number; onResend: () => void; onChange: () => void }) {
  const [now, setNow] = useState(() => Date.now());
  const wait = Math.max(0, Math.ceil((sentAt + RESEND_COOLDOWN_MS - now) / 1000));
  const timer = useRef<number | null>(null);
  useEffect(() => {
    if (wait <= 0) return;
    timer.current = window.setTimeout(() => setNow(Date.now()), 1000);
    return () => {
      if (timer.current != null) window.clearTimeout(timer.current);
    };
  }, [wait, now]);

  return (
    <div className="stack account__sent" data-testid="account-email-sent" role="status">
      <p style={{ margin: 0 }}>Check your inbox — the link works for 20 minutes.</p>
      <p className="small muted" style={{ margin: 0 }}>
        Sent to <span className="account__to">{to}</span>.{' '}
        <button type="button" className="link" onClick={onChange} data-testid="account-email-change">
          Use a different address
        </button>
      </p>
      <Button block disabled={wait > 0} onClick={onResend} testId="account-email-resend">
        {wait > 0 ? `Send again in ${wait}s` : 'Send again'}
      </Button>
    </div>
  );
}

/**
 * The compact prompt the match-end screen and the Profile sheet put in front of a guest. Owns its
 * own sheet, so a host only has to decide where it sits. Renders nothing unless `useClaimOffer`
 * says yes, and reports `claim_prompt_shown` once per mount, the first time it actually appears.
 */
export function SaveProgressCta({ where, level, config }: { where: ClaimWhere; level?: number | null; config?: AuthConfig }) {
  const offer = useClaimOffer(config);
  const [open, setOpen] = useState(false);
  const reported = useRef(false);

  useEffect(() => {
    if (!offer || reported.current) return;
    reported.current = true;
    trackAccountEvent({ ev: 'claim_prompt_shown', props: { where } });
  }, [offer, where]);

  if (!offer) return null;
  return (
    <>
      <button type="button" className="setting-row setting-row--claim" data-testid={`claim-cta-${where}`} onClick={() => setOpen(true)}>
        <span className="setting-row__label">{level && level > 0 ? `Save your Level ${level} progress` : 'Save your progress'}</span>
        <span className="setting-row__chev" aria-hidden>
          ›
        </span>
      </button>
      <AccountSheet open={open} onClose={() => setOpen(false)} where={where} config={config} />
    </>
  );
}
